'use client';

import React, { useEffect, useRef } from 'react';

interface HeroSectionProps {
  onBookClick: () => void;
}

const BRAND_LETTERS = ['C', 'o', 'r', 's', 'A', 's', 'i', 'a'];

const HeroSection: React.FC<HeroSectionProps> = ({ onBookClick }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Parallax on mouse move
    const handleMouseMove = (e: MouseEvent) => {
      if (!overlayRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      overlayRef.current.style.transform = `translate(${x}px, ${y}px) scale(1.04)`;
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* ── Background Image Layer (parallax) ── */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={overlayRef}
          className="absolute inset-[-4%] transition-transform duration-[1200ms] ease-out"
          style={{ willChange: 'transform' }}>
          
          <img
            src="/assets/images/hero-bg.jpg"
            alt="Buffet asiatique avec plats variés"
            className="w-full h-full object-cover" />
          
        </div>
        {/* Atmospheric layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/50 to-charcoal/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/40 via-transparent to-charcoal/20" />
      </div>

      {/* ── Floating Ember Detail Cards ── */}
      <div className="absolute top-[22%] right-[8%] animate-float-slow hidden lg:block" style={{ animationDelay: '0s' }}>
        <div className="glass-dark rounded-2xl px-5 py-4 text-parchment">
          <p className="text-xs uppercase tracking-widest text-clay mb-1 font-sans">3 Villes</p>
          <p className="font-serif italic text-lg leading-tight">Ajaccio • Bastia • Porto-Vecchio</p>
        </div>
      </div>

      <div className="absolute bottom-[28%] left-[7%] animate-float-mid hidden lg:block" style={{ animationDelay: '1.5s' }}>
        <div className="glass-dark rounded-2xl px-5 py-4 text-parchment">
          <p className="text-xs uppercase tracking-widest text-clay mb-1 font-sans">Formule</p>
          <p className="font-serif italic text-lg leading-tight">Buffet à volonté</p>
        </div>
      </div>

      <div className="absolute top-[30%] left-[5%] animate-float-slow hidden xl:block" style={{ animationDelay: '3s' }}>
        <div className="glass-dark rounded-2xl px-5 py-4 text-parchment">
          <p className="text-xs uppercase tracking-widest text-clay mb-1 font-sans">6 Points de vente</p>
          <p className="font-serif italic text-lg leading-tight">Buffet • Traiteur • Épicerie</p>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Eyebrow */}
        <p
          className="clip-in stagger-1 text-clay text-xs uppercase tracking-widest-3 font-sans font-medium mb-8">

          Corse · 6 Points de Vente · Depuis 2020
        </p>

        {/* Brand Name — letter by letter */}
        <h1 className="font-serif italic mb-6" style={{ fontSize: 'clamp(5rem, 14vw, 11rem)', lineHeight: '0.9', color: '#F5F0E8' }}>
          {BRAND_LETTERS.map((letter, i) =>
          <span
            key={i}
            className="letter-reveal inline-block"
            style={{ animationDelay: `${0.3 + i * 0.12}s` }}>
            
              {letter}
            </span>
          )}
        </h1>

        {/* Tagline */}
        <p
          className="clip-in stagger-4 font-sans text-parchment/70 text-lg md:text-xl max-w-xl mx-auto leading-relaxed mb-12">

          Buffet d'Asie — restaurants buffet à volonté. Market Traiteur — plats à emporter et épicerie asiatique. Saveurs authentiques et produits du terroir corse.
        </p>

        {/* CTAs */}
        <div className="clip-in stagger-5 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onBookClick}
            className="bg-corsasia text-parchment px-10 py-4 rounded-full text-sm uppercase tracking-widest font-semibold hover:bg-corsasia-dark transition-all duration-300 shadow-xl shadow-corsasia/30 ember-pulse">

            Réserver
          </button>
          <a
            href="#menu"
            className="text-parchment/70 text-sm uppercase tracking-widest font-medium border-b border-parchment/30 pb-0.5 hover:text-parchment hover:border-parchment/70 transition-all duration-300">

            Voir la carte
          </a>
        </div>
      </div>

      {/* ── Scroll Indicator ── */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <p className="text-parchment/40 text-xs uppercase tracking-widest font-sans">Défiler</p>
        <div className="scroll-indicator w-px h-12 bg-gradient-to-b from-clay/60 to-transparent" />
      </div>

      {/* ── Grain overlay ── */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
      }} />
      
    </section>);

};

export default HeroSection;